import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';

import { PropertyService } from '../services/property.service';
import { FeatureService } from '../services/feature.service';
import { PapersService } from '../services/papers.service';
import { PropertyDetailMapper } from '../mappers/property-detail.mapper';
import { PropertySearchMapper } from '../mappers/property-search.mapper';

import {
    PropertyDetailVM,
    GalleryImageVM,
    MapLocationVM,
    PropertyDocumentVM,
} from '../models/property-detail.vm';
import { PropertySearchCardVM } from '../models/property-search-card.vm';
import { GroupResponseDto } from '../dto/group.response.dto';

/**
 * Orquesta el caso de uso "detalle de propiedad": detalle, galería,
 * ubicación, características, documentos y propiedades relacionadas.
 *
 * Única capa que toca services; el componente es UI y consume los `*$`.
 * Reemplaza la orquestación + mapeo que vivían en `PropertyDetailComponent`.
 * Provista a nivel del componente (vive mientras la pantalla está montada).
 */
@Injectable()
export class PropertyDetailFacade implements OnDestroy {

    private readonly detail = new BehaviorSubject<PropertyDetailVM | null>(null);
    private readonly gallery = new BehaviorSubject<GalleryImageVM[]>([]);
    private readonly features = new BehaviorSubject<GroupResponseDto[]>([]);
    private readonly location = new BehaviorSubject<MapLocationVM | null>(null);
    private readonly documents = new BehaviorSubject<PropertyDocumentVM[]>([]);
    private readonly related = new BehaviorSubject<PropertySearchCardVM[]>([]);
    private readonly loading = new BehaviorSubject<boolean>(false);
    private readonly error = new BehaviorSubject<string | null>(null);

    private readonly subs = new Subscription();

    readonly detail$ = this.detail.asObservable();
    readonly gallery$ = this.gallery.asObservable();
    readonly features$ = this.features.asObservable();
    readonly location$ = this.location.asObservable();
    readonly documents$ = this.documents.asObservable();
    readonly related$ = this.related.asObservable();
    readonly loading$ = this.loading.asObservable();
    readonly error$ = this.error.asObservable();

    constructor(
        private readonly propertyService: PropertyService,
        private readonly featureService: FeatureService,
        private readonly papersService: PapersService,
        private readonly mapper: PropertyDetailMapper,
        private readonly cardMapper: PropertySearchMapper,
    ) {}

    ngOnDestroy(): void {
        this.subs.unsubscribe();
    }

    /** Carga todo el detalle de una propiedad. Idempotente (re-llamable en retry). */
    load(propertyId: number): void {
        this.loading.next(true);
        this.error.next(null);

        // Colecciones independientes en paralelo; cada una tolera su propio error.
        this.subs.add(
            this.featureService.getMultimediaPropertyList(propertyId)
                .pipe(catchError(() => of([])))
                .subscribe((res) => this.gallery.next(this.mapper.toGallery(res))),
        );
        this.subs.add(
            this.featureService.getValueSecondaryListByPropertyId(propertyId)
                .pipe(catchError(() => of([])))
                .subscribe((res: GroupResponseDto[]) => this.features.next(res ?? [])),
        );
        this.subs.add(
            this.propertyService.getPropertyLocation(propertyId)
                .pipe(catchError(() => of(null)))
                .subscribe((res) => this.location.next(this.mapper.toLocation(res))),
        );
        this.subs.add(
            this.papersService.getPaperList(propertyId)
                .pipe(catchError(() => of([])))
                .subscribe((res) => this.documents.next(this.mapper.toDocuments(res))),
        );

        // Detalle principal: gobierna loading/error y dispara los relacionados.
        this.subs.add(
            this.propertyService.getDetailProperty(propertyId).pipe(
                map((dto) => this.mapper.toDetailVM(dto)),
                tap((vm) => {
                    this.detail.next(vm);
                    this.loadRelated(vm, propertyId);
                }),
                catchError(() => {
                    this.error.next('No pudimos cargar la propiedad. Inténtalo nuevamente.');
                    this.detail.next(null);
                    return of(null);
                }),
                finalize(() => this.loading.next(false)),
            ).subscribe(),
        );
    }

    /** "Descubre más": reusa la búsqueda por segmento (sin endpoint dedicado). */
    private loadRelated(vm: PropertyDetailVM | null, currentId: number): void {
        if (!vm?.segmentId) {
            this.related.next([]);
            return;
        }
        this.subs.add(
            this.propertyService.searchProperties({ segmentId: vm.segmentId, defaultPage: false }, 0, 12).pipe(
                map((page) => this.cardMapper.toPageVM(page).content),
                map((cards) => cards.filter((c) => c.propertyId !== currentId)),
                catchError(() => of([] as PropertySearchCardVM[])),
            ).subscribe((cards) => this.related.next(cards)),
        );
    }
}
